import { createContext, useContext } from "react"
import { z } from "zod"

import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { useSafeSessionStorage } from "~/hooks/useSafeLocalStorage"
import type { DemandScope } from "~/utils/demandProjection"

// The demand area's own per-collection editing scope, deliberately not the app-wide skuStore
// selectors (reusing them would cross-contaminate the global filter). null axis = "all". A stored
// entry failing the schema resets to DEFAULT_SCOPE.
const DemandScopeStateSchema: z.ZodType<DemandScope> = z.object({
  channel: z.string().nullable(),
  warehouseId: z.string().nullable(),
  marketId: z.string().nullable(),
})
const DEFAULT_SCOPE: DemandScope = { channel: null, warehouseId: null, marketId: null }

export function useDemandScopeState() {
  const { scopeId } = useDemand2Scope()
  return useSafeSessionStorage<DemandScope>(
    `demand2.${scopeId}.editScope`,
    DEFAULT_SCOPE,
    DemandScopeStateSchema,
  )
}

export function useDemandScope(): DemandScope {
  const [s] = useDemandScopeState()
  return s
}

/**
 * The filter of the table that opened the inspector, where it isn't the demand edit scope itself
 * (Performance filters by the app-wide warehouse and channel). Null = the edit scope.
 */
export const TableScopeContext = createContext<DemandScope | null>(null)

/** What a new adjustment's scope starts from: the opening table's filter. */
export function useAdjustmentDefaultScope(): DemandScope {
  const tableScope = useContext(TableScopeContext)
  const editScope = useDemandScope()
  return tableScope ?? editScope
}
